import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Scissors, Wand2, FileVideo, UploadCloud, Loader2, Download,
  Save, Play, X, CheckCircle2, RotateCcw, Volume2, Sparkles,
  Youtube, Calendar, Power, RefreshCw, Clock, Film, ExternalLink,
} from 'lucide-react';
import { useUserId } from '../context/UserAuthContext';

const API_BASE = window.location.origin;

/**
 * Clipping page — two tabs:
 *  1. Auto-Cut: upload video → Whisper transcribe → detect silences + filler
 *     words → user toggles which to remove → ffmpeg trim+concat on the backend.
 *  2. Transcode: upload video → choose target format / resolution / crf →
 *     ffmpeg conversion on the backend.
 * Both tabs end with download + save-to-portfolio actions.
 */
const Clipping = () => {
  const [tab, setTab] = useState('autocut');
  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-8" data-testid="clipping-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Scissors size={28} className="text-cyan-400" />
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Clipping</h1>
        </div>
        <p className="text-sm text-gray-400 mb-6">
          AI-powered auto-cut for silences &amp; filler words, plus on-demand video transcoding.
        </p>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-gray-800 overflow-x-auto">
          <TabBtn active={tab === 'autocut'} onClick={() => setTab('autocut')} icon={Wand2} label="AI Auto-Cut" testId="clipping-tab-autocut" />
          <TabBtn active={tab === 'transcode'} onClick={() => setTab('transcode')} icon={FileVideo} label="Transcode" testId="clipping-tab-transcode" />
          <TabBtn active={tab === 'automation'} onClick={() => setTab('automation')} icon={Youtube} label="YouTube Automation" testId="clipping-tab-automation" />
        </div>

        {tab === 'autocut' && <AutoCutTab />}
        {tab === 'transcode' && <TranscodeTab />}
        {tab === 'automation' && <AutomationTab />}
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
      active ? 'border-cyan-400 text-white' : 'border-transparent text-gray-400 hover:text-gray-100'
    }`}
  >
    <Icon size={16} />
    {label}
  </button>
);

// ===========================================================================
// AI AUTO-CUT TAB
// ===========================================================================

const AutoCutTab = () => {
  const userId = useUserId();
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [cuts, setCuts] = useState([]);          // detected ranges
  const [selected, setSelected] = useState({});  // { [index]: bool }
  const [videoUrl, setVideoUrl] = useState(null);
  const [outputId, setOutputId] = useState(null);
  const videoRef = useRef(null);

  const reset = () => {
    setFile(null); setFileId(null); setUploading(false); setTranscribing(false);
    setApplying(false); setDuration(0); setCuts([]); setSelected({});
    setVideoUrl(null); setOutputId(null);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setVideoUrl(URL.createObjectURL(f));
    setFileId(null); setCuts([]); setSelected({}); setOutputId(null);
  };

  const runPipeline = async () => {
    if (!file) return;
    setUploading(true);
    try {
      // 1. Upload
      const fd = new FormData();
      fd.append('file', file);
      const upRes = await fetch(`${API_BASE}/api/clipping/upload`, { method: 'POST', body: fd });
      if (!upRes.ok) throw new Error('Upload failed');
      const upJson = await upRes.json();
      setFileId(upJson.file_id);
      setUploading(false);

      // 2. Transcribe (Whisper)
      setTranscribing(true);
      const tFd = new FormData();
      tFd.append('file_id', upJson.file_id);
      const tRes = await fetch(`${API_BASE}/api/clipping/transcribe`, { method: 'POST', body: tFd });
      if (!tRes.ok) throw new Error(await tRes.text());
      const tJson = await tRes.json();
      setDuration(tJson.duration || 0);

      // 3. Detect
      const dRes = await fetch(`${API_BASE}/api/clipping/detect-cuts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ words: tJson.words || [], duration: tJson.duration || 0 }),
      });
      const dJson = await dRes.json();
      const all = [...(dJson.filler_cuts || []), ...(dJson.silence_cuts || [])].sort((a, b) => a.start - b.start);
      setCuts(all);
      // Pre-select everything — user can untick what to keep
      setSelected(Object.fromEntries(all.map((_, i) => [i, true])));
      setTranscribing(false);
      toast.success(`Found ${all.length} suggested cuts.`);
    } catch (err) {
      console.error(err);
      toast.error('AI auto-cut failed. Please retry.');
      setUploading(false); setTranscribing(false);
    }
  };

  const applyCuts = async () => {
    if (!fileId) return;
    setApplying(true);
    try {
      const chosen = cuts.filter((_, i) => selected[i]);
      const res = await fetch(`${API_BASE}/api/clipping/apply-cuts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_id: fileId, duration, cuts: chosen }),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      setOutputId(j.output_id);
      toast.success('Trimmed clip ready.');
    } catch (e) {
      console.error(e);
      toast.error('Apply cuts failed.');
    } finally {
      setApplying(false);
    }
  };

  const totalRemovedSec = cuts
    .filter((_, i) => selected[i])
    .reduce((sum, c) => sum + (c.end - c.start), 0);

  return (
    <div className="space-y-5">
      {!fileId && (
        <UploadDropzone onPick={onPick} file={file} />
      )}

      {file && !fileId && (
        <div className="flex items-center justify-end gap-2">
          <button onClick={reset} className="px-3 py-2 text-sm rounded-lg border border-gray-700 hover:border-gray-400">
            Cancel
          </button>
          <button
            onClick={runPipeline}
            disabled={uploading || transcribing}
            data-testid="clipping-autocut-start"
            className="px-4 py-2 text-sm rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 disabled:text-gray-400 flex items-center gap-2"
          >
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> :
             transcribing ? <><Loader2 size={14} className="animate-spin" /> Transcribing…</> :
             <><Wand2 size={14} /> Analyse with AI</>}
          </button>
        </div>
      )}

      {videoUrl && (
        <div className="bg-black border border-gray-800 rounded-xl overflow-hidden">
          <video ref={videoRef} src={videoUrl} controls className="w-full max-h-[420px] bg-black" />
        </div>
      )}

      {cuts.length > 0 && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3 bg-black border border-gray-800 rounded-xl p-4">
            <div>
              <div className="text-sm font-semibold">{cuts.length} suggested cuts</div>
              <div className="text-xs text-gray-400">
                Selected removes <span className="text-cyan-300">{totalRemovedSec.toFixed(1)}s</span> of <span className="text-gray-300">{duration.toFixed(1)}s</span>.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected(Object.fromEntries(cuts.map((_, i) => [i, true])))}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-700 hover:border-gray-400">
                Select all
              </button>
              <button
                onClick={() => setSelected({})}
                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-700 hover:border-gray-400">
                Clear
              </button>
              <button
                onClick={applyCuts}
                disabled={applying || totalRemovedSec === 0}
                data-testid="clipping-autocut-apply"
                className="text-sm px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 disabled:text-gray-400 flex items-center gap-2">
                {applying ? <><Loader2 size={14} className="animate-spin" /> Rendering…</> : <><Scissors size={14} /> Apply cuts</>}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {cuts.map((c, i) => (
              <label
                key={i}
                className={`flex items-start gap-2 border rounded-lg px-3 py-2 cursor-pointer text-sm ${
                  selected[i] ? 'border-cyan-500/60 bg-cyan-500/5' : 'border-gray-800'
                }`}
              >
                <input
                  type="checkbox"
                  checked={!!selected[i]}
                  onChange={() => setSelected((s) => ({ ...s, [i]: !s[i] }))}
                  className="mt-1 accent-cyan-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    {c.kind === 'filler' ? (
                      <span className="text-amber-300 uppercase tracking-wider">Filler</span>
                    ) : (
                      <span className="text-purple-300 uppercase tracking-wider flex items-center gap-1">
                        <Volume2 size={10} /> Silence
                      </span>
                    )}
                    <span className="text-gray-500">
                      {c.start.toFixed(2)}s → {c.end.toFixed(2)}s
                    </span>
                  </div>
                  <div className="text-sm truncate">{c.label}</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (videoRef.current) {
                      videoRef.current.currentTime = c.start;
                      videoRef.current.play();
                    }
                  }}
                  className="p-1 hover:bg-gray-900 rounded"
                  title="Preview"
                >
                  <Play size={12} className="text-gray-400" />
                </button>
              </label>
            ))}
          </div>
        </>
      )}

      {outputId && (
        <OutputCard outputId={outputId} ext="mp4" sourceLabel="auto-cut" duration={duration - totalRemovedSec} userId={userId} onReset={reset} />
      )}
    </div>
  );
};

// ===========================================================================
// TRANSCODE TAB
// ===========================================================================

const TranscodeTab = () => {
  const userId = useUserId();
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [outputId, setOutputId] = useState(null);
  const [outputExt, setOutputExt] = useState('mp4');

  // Settings
  const [targetFormat, setTargetFormat] = useState('mp4');
  const [resolution, setResolution] = useState('source');
  const [crf, setCrf] = useState(23);

  const reset = () => {
    setFile(null); setFileId(null); setUploading(false); setProcessing(false);
    setOutputId(null); setOutputExt('mp4');
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setFileId(null); setOutputId(null);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/api/clipping/upload`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const j = await res.json();
      setFileId(j.file_id);
    } catch (e) {
      console.error(e);
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const transcode = async () => {
    if (!fileId) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/api/clipping/transcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: fileId,
          target_format: targetFormat,
          resolution,
          crf,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      setOutputId(j.output_id);
      setOutputExt(j.ext);
      toast.success(`Converted to .${j.ext}`);
    } catch (e) {
      console.error(e);
      toast.error('Transcode failed.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-5">
      {!fileId && <UploadDropzone onPick={onPick} file={file} />}

      {file && !fileId && (
        <div className="flex items-center justify-end gap-2">
          <button onClick={reset} className="px-3 py-2 text-sm rounded-lg border border-gray-700 hover:border-gray-400">Cancel</button>
          <button
            onClick={upload}
            disabled={uploading}
            data-testid="clipping-transcode-upload"
            className="px-4 py-2 text-sm rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 flex items-center gap-2"
          >
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><UploadCloud size={14} /> Upload</>}
          </button>
        </div>
      )}

      {fileId && !outputId && (
        <div className="bg-black border border-gray-800 rounded-xl p-4 space-y-4">
          <div className="text-sm font-semibold flex items-center gap-2">
            <Sparkles size={14} className="text-cyan-400" />
            Conversion settings
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SelectField label="Format" value={targetFormat} onChange={setTargetFormat} options={[
              ['mp4', 'MP4 (H.264)'], ['webm', 'WebM (VP9)'], ['mov', 'MOV (H.264)'],
              ['mkv', 'MKV (H.264)'], ['mp3', 'MP3 (audio only)'], ['wav', 'WAV (audio only)'],
            ]} testId="clipping-transcode-format" />
            <SelectField label="Resolution" value={resolution} onChange={setResolution} options={[
              ['source', 'Source'], ['1080p', '1080p'], ['720p', '720p'], ['480p', '480p'], ['360p', '360p'],
            ]} disabled={['mp3', 'wav'].includes(targetFormat)} testId="clipping-transcode-resolution" />
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Quality (CRF)</div>
              <input
                type="range"
                min="18"
                max="32"
                value={crf}
                onChange={(e) => setCrf(Number(e.target.value))}
                className="w-full accent-cyan-500"
                disabled={['mp3', 'wav'].includes(targetFormat)}
              />
              <div className="text-xs text-gray-400 mt-1">
                {crf} — <span className="text-gray-500">{crf <= 20 ? 'archive' : crf <= 25 ? 'standard' : 'compressed'}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button onClick={reset} className="px-3 py-2 text-sm rounded-lg border border-gray-700 hover:border-gray-400 mr-2">
              <RotateCcw size={14} className="inline mr-1" /> Pick another file
            </button>
            <button
              onClick={transcode}
              disabled={processing}
              data-testid="clipping-transcode-go"
              className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700 flex items-center gap-2">
              {processing ? <><Loader2 size={14} className="animate-spin" /> Converting…</> : <><FileVideo size={14} /> Convert</>}
            </button>
          </div>
        </div>
      )}

      {outputId && (
        <OutputCard outputId={outputId} ext={outputExt} sourceLabel="transcoded" userId={userId} onReset={reset} />
      )}
    </div>
  );
};

// ===========================================================================
// Shared sub-components
// ===========================================================================

const UploadDropzone = ({ onPick, file }) => (
  <label
    htmlFor="clipping-file-input"
    className="block border-2 border-dashed border-gray-700 hover:border-gray-400 rounded-2xl p-8 text-center cursor-pointer transition-colors bg-black"
  >
    <UploadCloud size={32} className="mx-auto text-gray-500 mb-2" />
    <div className="text-sm font-semibold">{file ? file.name : 'Drop a video here, or click to choose'}</div>
    <div className="text-xs text-gray-500 mt-1">
      MP4, MOV, WebM, MKV — up to a few hundred MB.
    </div>
    <input
      id="clipping-file-input"
      type="file"
      accept="video/*"
      className="hidden"
      onChange={onPick}
      data-testid="clipping-file-input"
    />
  </label>
);

const SelectField = ({ label, value, onChange, options, disabled, testId }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={testId}
      className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 text-sm focus:border-gray-400 outline-none disabled:opacity-50"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

const OutputCard = ({ outputId, ext, sourceLabel, duration, userId, onReset }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [title, setTitle] = useState(`${sourceLabel === 'auto-cut' ? 'Auto-cut' : 'Transcoded'} ${new Date().toLocaleDateString()}`);

  const downloadUrl = `${API_BASE}/api/clipping/download/${outputId}`;

  const saveToPortfolio = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/clipping/save-to-portfolio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ output_id: outputId, user_id: userId, title, duration, category: 'Clips' }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSaved(true);
      toast.success('Saved to Media Portfolio.');
    } catch (e) {
      console.error(e);
      toast.error('Save to portfolio failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-black border border-emerald-500/40 rounded-xl p-4 space-y-3" data-testid="clipping-output-card">
      <div className="flex items-center gap-2 text-emerald-300 text-sm">
        <CheckCircle2 size={16} /> Output ready (.{ext})
      </div>
      <video src={downloadUrl} controls className="w-full max-h-[360px] rounded-lg bg-black" />
      <div className="flex items-center gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title to save"
          className="flex-1 bg-gray-950 border border-gray-800 focus:border-gray-500 rounded-lg px-3 py-2 text-sm outline-none"
          data-testid="clipping-output-title"
        />
        <a
          href={downloadUrl}
          download
          data-testid="clipping-output-download"
          className="text-sm px-3 py-2 rounded-lg border border-gray-700 hover:border-gray-400 flex items-center gap-2"
        >
          <Download size={14} /> Download
        </a>
        <button
          onClick={saveToPortfolio}
          disabled={saving || saved}
          data-testid="clipping-output-save"
          className={`text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${
            saved ? 'bg-emerald-700 text-white' : 'bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-700'
          }`}
        >
          {saved ? <><CheckCircle2 size={14} /> Saved</> : saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save to Portfolio</>}
        </button>
      </div>
      <div className="flex justify-end">
        <button onClick={onReset} className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1">
          <X size={12} /> Start over
        </button>
      </div>
    </div>
  );
};

export default Clipping;

// ===========================================================================
// YOUTUBE AUTOMATION PIPELINE TAB
// ===========================================================================

const DEFAULT_CFG = {
  enabled: false,
  playlist_url: '',
  schedule_hour_utc: 6,
  max_videos_per_run: 5,
  max_clips_per_video: 3,
  clip_duration_sec: 45,
  vertical: true,
};

const AutomationTab = () => {
  const userId = useUserId();
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);
  const [clips, setClips] = useState([]);
  const [activeRun, setActiveRun] = useState(null); // run object currently being polled
  const [cookiesStatus, setCookiesStatus] = useState({ installed: false, size_bytes: 0 });
  const [cookiesText, setCookiesText] = useState('');
  const [showCookies, setShowCookies] = useState(false);
  const [savingCookies, setSavingCookies] = useState(false);

  // Input mode: 'playlist' | 'url' | 'upload' | 'restream'
  const [inputMode, setInputMode] = useState('playlist');
  const [singleUrl, setSingleUrl] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Restream
  const [restreamStatus, setRestreamStatus] = useState({ connected: false, configured: false });
  const [restreamEvents, setRestreamEvents] = useState([]);
  const [restreamEventsLoading, setRestreamEventsLoading] = useState(false);
  const [restreamRecordings, setRestreamRecordings] = useState({}); // {eventId: [recordings]}
  const [restreamLoadingEventId, setRestreamLoadingEventId] = useState(null);
  const [restreamSelectedEvent, setRestreamSelectedEvent] = useState(null);
  // Restream Clips API
  const [restreamSubtab, setRestreamSubtab] = useState('recordings'); // 'recordings' | 'clips' | 'drive'
  const [restreamProjects, setRestreamProjects] = useState([]);
  const [restreamProjectsLoading, setRestreamProjectsLoading] = useState(false);
  const [restreamProjectDetails, setRestreamProjectDetails] = useState({}); // {projectId: {clips, posted_clips}}
  const [restreamLoadingProjectId, setRestreamLoadingProjectId] = useState(null);
  const [restreamSelectedProject, setRestreamSelectedProject] = useState(null);
  const [savingClipIds, setSavingClipIds] = useState(new Set());
  // Watch modal
  const [watchClip, setWatchClip] = useState(null); // {clip, project, url?, type?}
  const [watchLoading, setWatchLoading] = useState(false);
  // Google Drive
  const [gdriveStatus, setGdriveStatus] = useState({ connected: false, configured: false });
  const [gdriveFiles, setGdriveFiles] = useState([]);
  const [gdriveLoading, setGdriveLoading] = useState(false);
  const [gdrivePolling, setGdrivePolling] = useState(false);
  // Descript
  const [descriptStatus, setDescriptStatus] = useState({ configured: false, reachable: false });
  const [descriptProjects, setDescriptProjects] = useState([]);
  const [descriptSendingId, setDescriptSendingId] = useState(null);
  const [descriptMenuFor, setDescriptMenuFor] = useState(null); // drive file_id with open menu

  // ---- loaders --------------------------------------------------------------
  const loadAll = async () => {
    if (!userId) return;
    try {
      const [cRes, rRes, clRes, ckRes, rsRes, gdRes, dxRes, dxpRes] = await Promise.all([
        fetch(`${API_BASE}/api/clipping/pipeline/config?user_id=${userId}`),
        fetch(`${API_BASE}/api/clipping/pipeline/runs?user_id=${userId}&limit=10`),
        fetch(`${API_BASE}/api/clipping/pipeline/clips?user_id=${userId}&limit=24`),
        fetch(`${API_BASE}/api/clipping/pipeline/cookies-status`),
        fetch(`${API_BASE}/api/restream/status?user_id=${userId}`),
        fetch(`${API_BASE}/api/gdrive/status?user_id=${userId}`),
        fetch(`${API_BASE}/api/descript/status`),
        fetch(`${API_BASE}/api/descript/projects?user_id=${userId}`),
      ]);
      if (cRes.ok) {
        const data = await cRes.json();
        setCfg({ ...DEFAULT_CFG, ...data });
      }
      if (rRes.ok) {
        const data = await rRes.json();
        setRuns(data.runs || []);
        const live = (data.runs || []).find(r => r.status === 'running');
        if (live) setActiveRun(live);
      }
      if (clRes.ok) {
        const data = await clRes.json();
        setClips(data.clips || []);
      }
      if (ckRes.ok) {
        setCookiesStatus(await ckRes.json());
      }
      if (rsRes.ok) {
        setRestreamStatus(await rsRes.json());
      }
      if (gdRes.ok) {
        setGdriveStatus(await gdRes.json());
      }
      if (dxRes.ok) {
        setDescriptStatus(await dxRes.json());
      }
      if (dxpRes.ok) {
        const d = await dxpRes.json();
        setDescriptProjects(d?.projects || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [userId]);

  // Poll active run every 4s until it ends.
  useEffect(() => {
    if (!activeRun || !userId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/clipping/pipeline/runs?user_id=${userId}&limit=10`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setRuns(data.runs || []);
        const updated = (data.runs || []).find(r => r.id === activeRun.id);
        if (updated && updated.status !== 'running') {
          setActiveRun(null);
          // refresh clips on completion
          const clRes = await fetch(`${API_BASE}/api/clipping/pipeline/clips?user_id=${userId}&limit=24`);
          if (clRes.ok) {
            const cd = await clRes.json();
            setClips(cd.clips || []);
          }
          if (updated.status === 'done') {
            toast.success(`Pipeline finished — ${updated.clips_created || 0} clip(s) created`);
          } else if (updated.status === 'error') {
            toast.error('Pipeline failed — check the run log');
          }
        } else if (updated) {
          setActiveRun(updated);
        }
      } catch (e) { /* swallow */ }
    };
    const interval = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [activeRun?.id, userId]); // eslint-disable-line

  // ---- handlers -------------------------------------------------------------
  const saveCfg = async (overrides = {}) => {
    if (!userId) return;
    setSaving(true);
    try {
      const payload = { ...cfg, ...overrides, user_id: userId };
      const res = await fetch(`${API_BASE}/api/clipping/pipeline/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      const data = await res.json();
      setCfg({ ...DEFAULT_CFG, ...data.config });
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!userId) return;
    if (!cfg.playlist_url) {
      toast.error('Add a playlist URL first');
      return;
    }
    setRunning(true);
    try {
      // Persist current settings, then trigger a run
      await saveCfg();
      const res = await fetch(`${API_BASE}/api/clipping/pipeline/run-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Run failed');
      }
      const data = await res.json();
      toast.success('Pipeline started');
      // seed the run we will poll
      setActiveRun({ id: data.run_id, status: 'running', log: [], clips_created: 0, videos_processed: 0, started_at: new Date().toISOString() });
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const markPosted = async (clip, posted) => {
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/api/clipping/pipeline/clips/mark-posted`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: clip.id, user_id: userId, posted }),
      });
      setClips(prev => prev.map(c => c.id === clip.id ? { ...c, posted } : c));
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const runSingleUrl = async () => {
    if (!userId) return;
    const url = singleUrl.trim();
    if (!url) { toast.error('Paste a YouTube video URL first'); return; }
    setRunning(true);
    try {
      // Persist current settings first so clip duration / 9:16 are honored
      await saveCfg();
      const res = await fetch(`${API_BASE}/api/clipping/pipeline/run-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, url }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Run failed');
      toast.success('Processing video — watch the log');
      setActiveRun({ id: data.run_id, status: 'running', log: [], clips_created: 0, videos_processed: 0, started_at: new Date().toISOString() });
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const runUpload = async () => {
    if (!userId) return;
    if (!uploadFile) { toast.error('Choose a video file first'); return; }
    setUploadingFile(true);
    setUploadProgress(0);
    try {
      // Step 1: upload to the existing /api/clipping/upload endpoint.
      const fileId = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE}/api/clipping/upload`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText).file_id); }
            catch { reject(new Error('Upload returned invalid JSON')); }
          } else {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        const fd = new FormData();
        fd.append('file', uploadFile);
        xhr.send(fd);
      });

      // Step 2: persist current settings, then kick off the per-file run.
      await saveCfg();
      const res = await fetch(`${API_BASE}/api/clipping/pipeline/run-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          file_id: fileId,
          title: uploadFile.name.replace(/\.[^.]+$/, ''),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Run failed');
      toast.success('Processing your video — watch the log');
      setActiveRun({ id: data.run_id, status: 'running', log: [], clips_created: 0, videos_processed: 0, started_at: new Date().toISOString() });
      setUploadFile(null);
      setUploadProgress(0);
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setUploadingFile(false);
    }
  };

  // === Restream handlers ===
  // Defensive fetch helper — uses XMLHttpRequest instead of window.fetch
  // because the platform's rrweb-recorder monkey-patches fetch and
  // intermittently throws "Response body is already used" / "Failed to
  // execute 'clone' on 'Response'" errors when it races our body reads.
  // XHR is not instrumented the same way → reliable.
  const _rsFetch = (url, opts = {}) => new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const method = (opts.method || 'GET').toUpperCase();
    xhr.open(method, url);
    if (opts.headers) {
      Object.entries(opts.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    }
    xhr.onload = () => {
      let data = null;
      const txt = xhr.responseText;
      if (txt) {
        try { data = JSON.parse(txt); } catch { data = { detail: txt }; }
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, data: { detail: 'Network error' } });
    xhr.send(opts.body || null);
  });

  const connectRestream = async () => {
    if (!userId) return;
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/auth-url?user_id=${userId}`);
      if (!ok || !data?.url) throw new Error(data?.detail || 'Auth URL failed');
      const popup = window.open(data.url, 'restream_oauth', 'width=600,height=720');
      if (!popup) {
        toast.error('Popup blocked — allow popups and retry');
      }
    } catch (e) {
      toast.error(e.message || 'Connect failed');
    }
  };

  const disconnectRestream = async () => {
    if (!userId) return;
    try {
      await _rsFetch(`${API_BASE}/api/restream/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      setRestreamStatus({ ...restreamStatus, connected: false });
      setRestreamEvents([]);
      setRestreamRecordings({});
      setRestreamSelectedEvent(null);
      toast.success('Restream disconnected');
    } catch (e) {
      toast.error('Disconnect failed');
    }
  };

  const loadRestreamEvents = async () => {
    if (!userId) return;
    setRestreamEventsLoading(true);
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/events?user_id=${userId}&limit=25`);
      if (!ok) throw new Error(data?.detail || 'Failed to load events');
      setRestreamEvents(data?.events || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load events');
    } finally {
      setRestreamEventsLoading(false);
    }
  };

  const loadRestreamRecordings = async (eventId) => {
    if (!userId) return;
    setRestreamLoadingEventId(eventId);
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/events/${eventId}/recordings?user_id=${userId}`);
      if (!ok) throw new Error(data?.detail || 'Failed to load recordings');
      setRestreamRecordings(prev => ({ ...prev, [eventId]: data?.recordings || [] }));
      setRestreamSelectedEvent(eventId);
    } catch (e) {
      toast.error(e.message || 'Failed to load recordings');
    } finally {
      setRestreamLoadingEventId(null);
    }
  };

  const clipRestreamRecording = async (recording, eventTitle) => {
    if (!userId) return;
    setRunning(true);
    try {
      await saveCfg();
      const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/clip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          file_id: recording.file_id,
          title: eventTitle || recording.name,
        }),
      });
      if (!ok) throw new Error(data?.detail || 'Run failed');
      toast.success('Pulling recording from Restream — watch the log');
      setActiveRun({ id: data.run_id, status: 'running', log: [], clips_created: 0, videos_processed: 0, started_at: new Date().toISOString() });
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  // === Restream Clips API handlers ===
  const loadRestreamProjects = async () => {
    if (!userId) return;
    setRestreamProjectsLoading(true);
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/clips/projects?user_id=${userId}&limit=20`);
      if (!ok) throw new Error(data?.detail || 'Failed to load clip projects');
      setRestreamProjects(data?.projects || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load clip projects');
    } finally {
      setRestreamProjectsLoading(false);
    }
  };

  const loadRestreamProjectDetails = async (projectId) => {
    if (!userId) return;
    setRestreamLoadingProjectId(projectId);
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/clips/projects/${projectId}?user_id=${userId}`);
      if (!ok) throw new Error(data?.detail || 'Failed to load clips');
      setRestreamProjectDetails(prev => ({ ...prev, [projectId]: data }));
      setRestreamSelectedProject(projectId);
    } catch (e) {
      toast.error(e.message || 'Failed to load clips');
    } finally {
      setRestreamLoadingProjectId(null);
    }
  };

  const watchRestreamClip = async (clip, project) => {
    if (!userId) return;
    // Try direct URL candidates first (if Restream returned any in the project details).
    const direct = Object.values(clip.url_candidates || {}).find(u => typeof u === 'string');
    if (direct) {
      setWatchClip({ clip, project, url: direct, type: 'video' });
      return;
    }
    setWatchLoading(true);
    setWatchClip({ clip, project, url: null, type: null });
    try {
      const { ok, data } = await _rsFetch(
        `${API_BASE}/api/restream/clips/projects/${project.id}/clips/${clip.clip_id}/playback?user_id=${userId}`
      );
      if (!ok) throw new Error(data?.detail || 'Could not resolve playback URL');
      setWatchClip({ clip, project, url: data.url, type: data.type });
    } catch (e) {
      toast.error(e.message || 'Could not load clip');
      setWatchClip(null);
    } finally {
      setWatchLoading(false);
    }
  };


  const saveRestreamClipToLibrary = async (clip, project) => {
    if (!userId) return;
    setSavingClipIds(prev => new Set(prev).add(clip.clip_id));
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/clips/save-to-library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          clip,
          project: { id: project.id, title: project.title },
        }),
      });
      if (!ok) throw new Error(data?.detail || 'Save failed');
      if (data?.duplicate) {
        toast.success('Already in your library');
      } else {
        toast.success('Saved to Auto-Clips');
      }
      // refresh portfolio gallery
      const { ok: cOk, data: cData } = await _rsFetch(`${API_BASE}/api/clipping/pipeline/clips?user_id=${userId}&limit=24`);
      if (cOk) setClips(cData?.clips || []);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSavingClipIds(prev => {
        const next = new Set(prev);
        next.delete(clip.clip_id);
        return next;
      });
    }
  };

  // Popup → parent message: refresh Restream status when OAuth completes.
  useEffect(() => {
    const onMsg = async (evt) => {
      if (evt.data && evt.data.type === 'restream-connected') {
        if (evt.data.ok) toast.success('Restream connected');
        try {
          const { ok, data } = await _rsFetch(`${API_BASE}/api/restream/status?user_id=${userId}`);
          if (ok && data) setRestreamStatus(data);
        } catch (e) { /* swallow */ }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line
  }, [userId]);



  // === Google Drive handlers ===
  const connectGdrive = async () => {
    if (!userId) return;
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/gdrive/auth-url?user_id=${userId}`);
      if (!ok || !data?.url) throw new Error(data?.detail || 'Auth URL failed');
      const popup = window.open(data.url, 'gdrive_oauth', 'width=600,height=720');
      if (!popup) toast.error('Popup blocked — allow popups and retry');
    } catch (e) {
      toast.error(e.message || 'Connect failed');
    }
  };

  const disconnectGdrive = async () => {
    if (!userId) return;
    try {
      await _rsFetch(`${API_BASE}/api/gdrive/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      setGdriveStatus({ ...gdriveStatus, connected: false, folder_id: null, folder_name: null, folder_url: null });
      setGdriveFiles([]);
      toast.success('Drive disconnected');
    } catch (e) {
      toast.error('Disconnect failed');
    }
  };

  const loadGdriveFiles = async () => {
    if (!userId) return;
    setGdriveLoading(true);
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/gdrive/files?user_id=${userId}`);
      if (!ok) throw new Error(data?.detail || 'Failed to load Drive files');
      setGdriveFiles(data?.files || []);
      // Refresh folder info in case it was just provisioned.
      if (data?.folder_id && data.folder_id !== gdriveStatus.folder_id) {
        setGdriveStatus(prev => ({ ...prev, folder_id: data.folder_id, folder_url: `https://drive.google.com/drive/folders/${data.folder_id}` }));
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load Drive files');
    } finally {
      setGdriveLoading(false);
    }
  };

  const pollGdrive = async () => {
    if (!userId) return;
    setGdrivePolling(true);
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/gdrive/poll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!ok) throw new Error(data?.detail || 'Sync failed');
      if (data.saved > 0) {
        toast.success(`Synced — ${data.saved} new clip${data.saved === 1 ? '' : 's'} added to Auto-Clips`);
      } else {
        toast.success(`Up to date — ${data.checked || 0} clip${data.checked === 1 ? '' : 's'} in folder`);
      }
      await loadGdriveFiles();
      // Refresh portfolio gallery so new clips appear there too.
      const { ok: cOk, data: cData } = await _rsFetch(`${API_BASE}/api/clipping/pipeline/clips?user_id=${userId}&limit=24`);
      if (cOk) setClips(cData?.clips || []);
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setGdrivePolling(false);
    }
  };

  // Popup → parent: refresh Drive status when OAuth completes
  useEffect(() => {
    const onMsg = async (evt) => {
      if (evt.data && evt.data.type === 'gdrive-connected') {
        if (evt.data.ok) toast.success('Google Drive connected');
        try {
          const { ok, data } = await _rsFetch(`${API_BASE}/api/gdrive/status?user_id=${userId}`);
          if (ok && data) setGdriveStatus(data);
        } catch (e) { /* swallow */ }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line
  }, [userId]);

  // === Descript ===
  const sendToDescript = async (driveFile, action) => {
    if (!userId) return;
    setDescriptSendingId(driveFile.file_id);
    setDescriptMenuFor(null);
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/descript/import-from-drive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          drive_file_id: driveFile.file_id,
          name: driveFile.name,
          action,
        }),
      });
      if (!ok) throw new Error(data?.detail || 'Send failed');
      const niceAction = action === 'edit' ? 'editor'
        : action === 'auto-clips' ? 'auto-clips'
        : 'studio sound + captions';
      toast.success(`Sent to Descript (${niceAction})`);
      // Refresh project list
      const { ok: pOk, data: pData } = await _rsFetch(`${API_BASE}/api/descript/projects?user_id=${userId}`);
      if (pOk) setDescriptProjects(pData?.projects || []);
    } catch (e) {
      toast.error(e.message || 'Send failed');
    } finally {
      setDescriptSendingId(null);
    }
  };

  const refreshDescriptProjects = async () => {
    if (!userId) return;
    try {
      const { ok, data } = await _rsFetch(`${API_BASE}/api/descript/projects?user_id=${userId}`);
      if (ok) setDescriptProjects(data?.projects || []);
    } catch (e) { /* swallow */ }
  };

  // Close the Descript action menu when clicking elsewhere
  useEffect(() => {
    if (!descriptMenuFor) return undefined;
    const onClick = () => setDescriptMenuFor(null);
    // Defer to avoid catching the click that opened the menu.
    const t = setTimeout(() => document.addEventListener('click', onClick), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', onClick); };
  }, [descriptMenuFor]);

  const saveCookies = async () => {
    const txt = cookiesText.trim();
    if (!txt) {
      toast.error('Paste your cookies.txt contents first');
      return;
    }
    setSavingCookies(true);
    try {
      const res = await fetch(`${API_BASE}/api/clipping/pipeline/cookies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cookies_text: txt, user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setCookiesStatus({ installed: true, size_bytes: data.size_bytes });
      setCookiesText('');
      setShowCookies(false);
      toast.success('Cookies installed — try Run now again');
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setSavingCookies(false);
    }
  };

  const deleteCookies = async () => {
    try {
      await fetch(`${API_BASE}/api/clipping/pipeline/cookies`, { method: 'DELETE' });
      setCookiesStatus({ installed: false, size_bytes: 0 });
      toast.success('Cookies removed');
    } catch (e) {
      toast.error('Remove failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading automation…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clipping-automation">
      {/* Cookies / Auth status banner */}
      <div
        className={`rounded-xl p-4 border ${
          cookiesStatus.installed
            ? 'bg-emerald-950/30 border-emerald-800'
            : 'bg-amber-950/30 border-amber-800'
        }`}
        data-testid="automation-cookies-banner"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 text-sm">
            <div className="font-semibold mb-1">
              {cookiesStatus.installed
                ? '✓ YouTube cookies installed'
                : '⚠ YouTube cookies not installed'}
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">
              {cookiesStatus.installed ? (
                <>The pipeline will run as your logged-in YouTube session ({(cookiesStatus.size_bytes / 1024).toFixed(1)} KB). If runs start failing, the cookies probably expired — re-export and upload.</>
              ) : (
                <>YouTube blocks server downloads in 2026. To unlock the pipeline, give it your logged-in YouTube cookies — takes ~2 minutes, one time.</>
              )}
            </p>
            {!cookiesStatus.installed && (
              <details className="mt-2 text-xs text-gray-300 group" data-testid="automation-cookies-howto">
                <summary className="cursor-pointer text-amber-300 hover:text-amber-200 select-none list-none flex items-center gap-1">
                  <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                  Step-by-step (2 min)
                </summary>
                <ol className="mt-2 ml-4 space-y-1.5 list-decimal text-[11px] leading-relaxed text-gray-300">
                  <li>
                    Install the Chrome extension <a
                      href="https://chromewebstore.google.com/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc"
                      target="_blank" rel="noreferrer"
                      className="text-cyan-300 hover:text-cyan-200 underline"
                    >“Get cookies.txt LOCALLY”</a> (Firefox users: <a
                      href="https://addons.mozilla.org/en-US/firefox/addon/cookies-txt/"
                      target="_blank" rel="noreferrer"
                      className="text-cyan-300 hover:text-cyan-200 underline"
                    >cookies.txt</a>). Pin it so the icon shows in the toolbar.
                  </li>
                  <li>Open a new tab → <code className="bg-black px-1 py-0.5 rounded">youtube.com</code>. Make sure you’re signed in (top-right shows your avatar).</li>
                  <li>Click the cookie icon in the toolbar → <strong>“Export As → Current Site”</strong>. A <code className="bg-black px-1 py-0.5 rounded">youtube.com_cookies.txt</code> file downloads.</li>
                  <li>Open that file (TextEdit opens it). It starts with <code className="bg-black px-1 py-0.5 rounded"># Netscape HTTP Cookie File</code>. Select All (⌘A) → Copy (⌘C).</li>
                  <li>Click <strong>“Paste cookies”</strong> on the right → paste into the box → <strong>Install cookies</strong>.</li>
                  <li>Hit <strong>Run now</strong>. 🎬</li>
                </ol>
                <p className="mt-2 text-[10px] text-gray-500">
                  Cookies are stored only on your OPERATR server. They expire every few weeks — just re-export and paste fresh ones if Run now starts failing again.
                </p>
              </details>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCookies(s => !s)}
              data-testid="automation-toggle-cookies"
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-700 hover:border-gray-400"
            >
              {showCookies ? 'Hide' : cookiesStatus.installed ? 'Replace' : 'Paste cookies'}
            </button>
            {cookiesStatus.installed && (
              <button
                type="button"
                onClick={deleteCookies}
                data-testid="automation-delete-cookies"
                className="text-xs px-3 py-1.5 rounded-lg border border-red-800 text-red-300 hover:border-red-500"
              >
                Remove
              </button>
            )}
          </div>
        </div>
        {showCookies && (
          <div className="mt-3 space-y-2">
            <textarea
              value={cookiesText}
              onChange={(e) => setCookiesText(e.target.value)}
              placeholder="# Netscape HTTP Cookie File&#10;.youtube.com&#9;TRUE&#9;/&#9;TRUE&#9;…"
              data-testid="automation-cookies-textarea"
              rows={6}
              className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-cyan-400"
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] text-gray-500">
                Stored at <code>/app/backend/secrets/youtube_cookies.txt</code> on this server only. Never logged or sent anywhere else.
              </p>
              <button
                type="button"
                onClick={saveCookies}
                disabled={savingCookies}
                data-testid="automation-save-cookies"
                className="text-xs px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 flex items-center gap-2 disabled:bg-gray-700"
              >
                {savingCookies ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Install cookies
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="bg-gray-950 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Youtube size={18} className="text-red-500" />
          <h2 className="text-lg font-semibold">Source → Auto-Clips</h2>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Three ways to feed the AI highlight + 9:16 vertical pipeline. Output lands in Media Portfolio → <span className="text-amber-400">Auto-Clips</span>.
        </p>

        {/* Source-mode picker */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5" data-testid="automation-source-modes">
          <SourceModeBtn
            active={inputMode === 'playlist'}
            onClick={() => setInputMode('playlist')}
            testId="automation-mode-playlist"
            icon={Calendar}
            title="Playlist (daily)"
            sub="Daily scrape your channel"
            warn={!cookiesStatus.installed}
          />
          <SourceModeBtn
            active={inputMode === 'url'}
            onClick={() => setInputMode('url')}
            testId="automation-mode-url"
            icon={Youtube}
            title="Single video URL"
            sub="Ad-hoc, one video at a time"
            warn={!cookiesStatus.installed}
          />
          <SourceModeBtn
            active={inputMode === 'upload'}
            onClick={() => setInputMode('upload')}
            testId="automation-mode-upload"
            icon={UploadCloud}
            title="Upload video file"
            sub="No YouTube auth needed"
          />
          <SourceModeBtn
            active={inputMode === 'restream'}
            onClick={() => {
              setInputMode('restream');
              if (restreamStatus.connected && restreamEvents.length === 0) loadRestreamEvents();
            }}
            testId="automation-mode-restream"
            icon={Film}
            title="Restream recordings"
            sub={restreamStatus.connected ? 'Connected — pick a stream' : 'Pull from your streams'}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* PLAYLIST MODE inputs */}
          {inputMode === 'playlist' && (
            <>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Playlist URL</label>
                <input
                  type="url"
                  value={cfg.playlist_url}
                  onChange={(e) => setCfg({ ...cfg, playlist_url: e.target.value })}
                  placeholder="https://www.youtube.com/playlist?list=…"
                  data-testid="automation-playlist-url"
                  className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400"
                />
                <p className="text-[11px] text-gray-500 mt-1">Use a playlist on your own channel — public or unlisted.</p>
              </div>
              <NumField label="Daily schedule (UTC hour)" value={cfg.schedule_hour_utc} min={0} max={23}
                        testId="automation-hour"
                        onChange={(v) => setCfg({ ...cfg, schedule_hour_utc: v })} />
              <NumField label="Max videos per run" value={cfg.max_videos_per_run} min={1} max={20}
                        testId="automation-max-videos"
                        onChange={(v) => setCfg({ ...cfg, max_videos_per_run: v })} />
            </>
          )}

          {/* URL MODE inputs */}
          {inputMode === 'url' && (
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-400 mb-1">YouTube video URL</label>
              <input
                type="url"
                value={singleUrl}
                onChange={(e) => setSingleUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=…  or  https://youtu.be/…"
                data-testid="automation-single-url"
                className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                One video, runs once. Same anti-bot rules apply — cookies recommended.
              </p>
            </div>
          )}

          {/* UPLOAD MODE inputs */}
          {inputMode === 'upload' && (
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-400 mb-1">Video file</label>
              <div className="bg-black border border-dashed border-gray-700 rounded-lg p-4">
                <input
                  type="file"
                  accept="video/*,.mp4,.mov,.webm,.mkv,.m4v"
                  data-testid="automation-upload-input"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-cyan-700 file:text-white hover:file:bg-cyan-600 cursor-pointer"
                />
                {uploadFile && (
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-gray-300 truncate" title={uploadFile.name}>
                      📁 {uploadFile.name} · {(uploadFile.size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                    {!uploadingFile && (
                      <button
                        type="button"
                        onClick={() => setUploadFile(null)}
                        className="text-gray-500 hover:text-gray-200"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
                {uploadingFile && (
                  <div className="mt-3">
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">Uploading… {uploadProgress}%</p>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                Drag any video you have locally — MP4, MOV, WebM, etc. Best for clips under 500&nbsp;MB.
                No YouTube auth needed for this path.
              </p>
            </div>
          )}

          {/* RESTREAM MODE */}
          {inputMode === 'restream' && (
            <div className="md:col-span-2" data-testid="automation-restream-panel">
              {!restreamStatus.connected ? (
                <div className="bg-black border border-gray-700 rounded-lg p-4 flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <div className="font-medium mb-0.5">Connect your Restream account</div>
                    <p className="text-xs text-gray-400">One-click OAuth — we’ll list your stream recordings so you can clip any of them.</p>
                  </div>
                  <button
                    type="button"
                    onClick={connectRestream}
                    data-testid="automation-restream-connect"
                    className="text-sm px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 flex items-center gap-2 whitespace-nowrap"
                  >
                    <ExternalLink size={14} /> Connect Restream
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-emerald-400">✓ Restream connected</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => restreamSubtab === 'recordings' ? loadRestreamEvents() : loadRestreamProjects()}
                        data-testid="automation-restream-reload"
                        className="px-2 py-1 rounded border border-gray-700 hover:border-gray-400 flex items-center gap-1"
                      >
                        <RefreshCw size={11} /> Reload
                      </button>
                      <button
                        type="button"
                        onClick={disconnectRestream}
                        data-testid="automation-restream-disconnect"
                        className="px-2 py-1 rounded border border-red-800 text-red-300 hover:border-red-500"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>

                  {/* Sub-tabs: Recordings vs AI Clips vs Drive */}
                  <div className="flex items-center gap-2 border-b border-gray-800 overflow-x-auto">
                    <button
                      type="button"
                      onClick={() => setRestreamSubtab('recordings')}
                      data-testid="automation-restream-subtab-recordings"
                      className={`px-3 py-1.5 text-xs border-b-2 -mb-px whitespace-nowrap ${
                        restreamSubtab === 'recordings'
                          ? 'border-cyan-400 text-white'
                          : 'border-transparent text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      Raw recordings
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRestreamSubtab('clips');
                        if (restreamProjects.length === 0) loadRestreamProjects();
                      }}
                      data-testid="automation-restream-subtab-clips"
                      className={`px-3 py-1.5 text-xs border-b-2 -mb-px flex items-center gap-1 whitespace-nowrap ${
                        restreamSubtab === 'clips'
                          ? 'border-purple-400 text-white'
                          : 'border-transparent text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      <Sparkles size={11} /> AI Clips
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-900/50 text-purple-300 ml-1">Restream-generated</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRestreamSubtab('drive');
                        if (gdriveStatus.connected && gdriveFiles.length === 0) loadGdriveFiles();
                      }}
                      data-testid="automation-restream-subtab-drive"
                      className={`px-3 py-1.5 text-xs border-b-2 -mb-px flex items-center gap-1 whitespace-nowrap ${
                        restreamSubtab === 'drive'
                          ? 'border-emerald-400 text-white'
                          : 'border-transparent text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      <FileVideo size={11} /> Drive Clips
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-900/50 text-emerald-300 ml-1">Zapier auto-sync</span>
                    </button>
                  </div>

                  {/* Plan / storage hint — shows if any recording came back as non-downloadable */}
                  {restreamSubtab === 'recordings' && (() => {
                    const allRecs = Object.values(restreamRecordings).flat();
                    const hasUndownloadable = allRecs.length > 0 && allRecs.some(r => !r.downloadable);
                    const allUndownloadable = allRecs.length > 0 && allRecs.every(r => !r.downloadable);
                    if (!hasUndownloadable) return null;
                    return (
                      <div className={`text-[11px] rounded-lg border px-3 py-2 ${
                        allUndownloadable ? 'border-amber-800 bg-amber-950/30 text-amber-200' : 'border-gray-800 text-gray-400'
                      }`}>
                        {allUndownloadable
                          ? <>Restream is showing recordings for these streams, but none of them are in your downloadable storage. This usually means your Restream plan doesn’t include cloud storage, or the broadcast wasn’t saved. Recordings from <strong>new</strong> broadcasts on Pro+ plans will appear here automatically.</>
                          : <>Some recordings aren’t in your Restream storage (older than 30 days, or not saved) — those will show a disabled <strong>Clip</strong> button. New recordings will work normally.</>
                        }
                      </div>
                    );
                  })()}

                  {restreamSubtab === 'recordings' && (restreamEventsLoading ? (
                    <div className="text-xs text-gray-400 flex items-center gap-2 py-2">
                      <Loader2 size={12} className="animate-spin" /> Loading your streams…
                    </div>
                  ) : restreamEvents.length === 0 ? (
                    <div className="text-xs text-gray-400 py-2">
                      No streams found. Click <strong>Reload events</strong> after broadcasting on Restream.
                    </div>
                  ) : (
                    <ul className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="automation-restream-events">
                      {restreamEvents.map(ev => {
                        const recs = restreamRecordings[ev.id];
                        const expanded = restreamSelectedEvent === ev.id;
                        return (
                          <li key={ev.id} className="border border-gray-800 rounded-lg overflow-hidden">
                            <button
                              type="button"
                              onClick={() => {
                                if (expanded) {
                                  setRestreamSelectedEvent(null);
                                } else if (recs) {
                                  setRestreamSelectedEvent(ev.id);
                                } else {
                                  loadRestreamRecordings(ev.id);
                                }
                              }}
                              data-testid={`automation-restream-event-${ev.id}`}
                              className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-900 text-left"
                            >
                              <div className="min-w-0">
                                <div className="text-sm truncate" title={ev.title}>{ev.title}</div>
                                <div className="text-[10px] text-gray-500">
                                  {ev.started_at ? new Date(ev.started_at).toLocaleString() : 'unknown date'}
                                  {ev.status ? ` · ${ev.status}` : ''}
                                </div>
                              </div>
                              {restreamLoadingEventId === ev.id ? (
                                <Loader2 size={12} className="animate-spin text-gray-400" />
                              ) : (
                                <span className="text-gray-500 text-xs">{expanded ? '▾' : '▸'}</span>
                              )}
                            </button>
                            {expanded && recs && (
                              <div className="border-t border-gray-800 bg-black/40">
                                {recs.length === 0 ? (
                                  <p className="text-[11px] text-gray-500 p-3">No recordings on this event yet.</p>
                                ) : (
                                  <ul className="divide-y divide-gray-900">
                                    {recs.map(rec => (
                                      <li key={rec.name} className="flex items-center justify-between gap-2 px-3 py-2">
                                        <div className="min-w-0 flex-1">
                                          <div className="text-xs truncate" title={rec.name}>{rec.name}</div>
                                          <div className="text-[10px] text-gray-500">
                                            {rec.kind}
                                            {rec.size_bytes ? ` · ${(rec.size_bytes/1024/1024).toFixed(1)} MB` : ''}
                                            {rec.duration_sec ? ` · ${Math.round(rec.duration_sec)}s` : ''}
                                            {!rec.downloadable ? ' · not in storage (may be expired)' : ''}
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => clipRestreamRecording(rec, ev.title)}
                                          disabled={running || !!activeRun || !rec.downloadable}
                                          title={rec.downloadable ? 'Run highlight pipeline' : 'No matching file in Restream storage — recording may have expired (30-day retention)'}
                                          data-testid={`automation-restream-clip-${rec.name}`}
                                          className="text-[11px] px-2 py-1 rounded-lg bg-cyan-600 hover:bg-cyan-500 flex items-center gap-1 disabled:bg-gray-700 disabled:opacity-60 whitespace-nowrap"
                                        >
                                          <Play size={10} /> Clip
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ))}

                  {/* AI CLIPS subtab */}
                  {restreamSubtab === 'clips' && (
                    restreamProjectsLoading ? (
                      <div className="text-xs text-gray-400 flex items-center gap-2 py-2">
                        <Loader2 size={12} className="animate-spin" /> Loading clip projects…
                      </div>
                    ) : restreamProjects.length === 0 ? (
                      <div className="text-xs text-gray-400 py-2 space-y-2">
                        <p>No clip projects yet.</p>
                        <p className="text-[11px] text-gray-500">
                          On Restream's web app, open any VOD and click <strong>"Generate clips"</strong>. Once their AI finishes, click <strong>Reload</strong> here and your clip projects will appear with virality scores + transcripts + thumbnails.
                        </p>
                      </div>
                    ) : (
                      <ul className="space-y-2 max-h-[28rem] overflow-y-auto pr-1" data-testid="automation-restream-projects">
                        {restreamProjects.map(proj => {
                          const details = restreamProjectDetails[proj.id];
                          const expanded = restreamSelectedProject === proj.id;
                          return (
                            <li key={proj.id} className="border border-gray-800 rounded-lg overflow-hidden">
                              <button
                                type="button"
                                onClick={() => {
                                  if (expanded) {
                                    setRestreamSelectedProject(null);
                                  } else if (details) {
                                    setRestreamSelectedProject(proj.id);
                                  } else {
                                    loadRestreamProjectDetails(proj.id);
                                  }
                                }}
                                data-testid={`automation-restream-project-${proj.id}`}
                                className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-900 text-left gap-3"
                              >
                                {proj.thumbnail_url && (
                                  <img src={proj.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded border border-gray-800 flex-shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm truncate" title={proj.title}>{proj.title}</div>
                                  <div className="text-[10px] text-gray-500">
                                    {proj.last_activity_at ? new Date(proj.last_activity_at).toLocaleString() : (proj.created_at ? new Date(proj.created_at).toLocaleString() : 'unknown')}
                                    {proj.clips_count != null ? ` · ${proj.clips_count} clip(s)` : ''}
                                    {proj.generating_now ? ' · generating…' : ''}
                                  </div>
                                </div>
                                {restreamLoadingProjectId === proj.id ? (
                                  <Loader2 size={12} className="animate-spin text-gray-400" />
                                ) : (
                                  <span className="text-gray-500 text-xs">{expanded ? '▾' : '▸'}</span>
                                )}
                              </button>

                              {expanded && details && (
                                <div className="border-t border-gray-800 bg-black/40 p-3 space-y-3">
                                  {(details.clips || []).length === 0 ? (
                                    <p className="text-[11px] text-gray-500">
                                      {details.generating_now
                                        ? 'Restream is still generating clips — click Reload in a minute.'
                                        : 'No clips on this project yet.'}
                                    </p>
                                  ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                      {details.clips.map(clip => {
                                        const saving = savingClipIds.has(clip.clip_id);
                                        const dur = (clip.start_sec != null && clip.end_sec != null)
                                          ? Math.round(clip.end_sec - clip.start_sec) : null;
                                        return (
                                          <div key={clip.clip_id} className="border border-gray-800 rounded-lg overflow-hidden bg-black flex flex-col">
                                            {clip.thumbnail_url ? (
                                              <img src={clip.thumbnail_url} alt={clip.name} className="w-full aspect-video object-cover" />
                                            ) : (
                                              <div className="w-full aspect-video bg-gray-900 flex items-center justify-center text-[10px] text-gray-600">no thumbnail</div>
                                            )}
                                            <div className="p-2 space-y-1 flex-1 flex flex-col">
                                              <div className="text-[12px] line-clamp-2" title={clip.name}>{clip.name}</div>
                                              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                                {clip.virality_score != null && (
                                                  <span className={`px-1.5 py-0.5 rounded font-medium ${
                                                    clip.virality_score >= 80 ? 'bg-emerald-950 text-emerald-300' :
                                                    clip.virality_score >= 60 ? 'bg-amber-950 text-amber-300' :
                                                    'bg-gray-900 text-gray-400'
                                                  }`}>
                                                    ✨ {clip.virality_score}
                                                  </span>
                                                )}
                                                {dur != null && <span>{dur}s</span>}
                                              </div>
                                              {clip.virality_reason && (
                                                <p className="text-[10px] text-gray-500 line-clamp-2" title={clip.virality_reason}>{clip.virality_reason}</p>
                                              )}
                                              <div className="flex-1" />
                                              <div className="mt-1 grid grid-cols-2 gap-1.5">
                                                <button
                                                  type="button"
                                                  onClick={() => watchRestreamClip(clip, proj)}
                                                  data-testid={`automation-restream-watch-${clip.clip_id}`}
                                                  className="text-[10px] px-2 py-1 rounded-lg border border-cyan-700 text-cyan-200 hover:bg-cyan-950 flex items-center justify-center gap-1"
                                                >
                                                  <Play size={10} /> Watch
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => saveRestreamClipToLibrary(clip, proj)}
                                                  disabled={saving}
                                                  data-testid={`automation-restream-save-${clip.clip_id}`}
                                                  className="text-[10px] px-2 py-1 rounded-lg border border-purple-700 text-purple-200 hover:bg-purple-950 disabled:opacity-50 flex items-center justify-center gap-1"
                                                >
                                                  {saving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                                                  Save
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {(details.posted_clips || []).length > 0 && (
                                    <div className="pt-2 border-t border-gray-900">
                                      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Posted</div>
                                      <ul className="space-y-0.5">
                                        {details.posted_clips.map((p, i) => (
                                          <li key={`${p.clip_id}-${i}`} className="text-[10px] text-gray-400 flex items-center gap-2">
                                            <span className="text-emerald-400">✓</span>
                                            {p.platform || 'platform'}
                                            {p.url && (
                                              <a href={p.url} target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">view</a>
                                            )}
                                            <span className="text-gray-600">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )
                  )}

                  {/* DRIVE subtab */}
                  {restreamSubtab === 'drive' && (
                    !gdriveStatus.connected ? (
                      <div className="space-y-3" data-testid="automation-gdrive-disconnected">
                        <div className="bg-black border border-gray-700 rounded-lg p-4 flex items-center justify-between gap-3">
                          <div className="text-sm min-w-0">
                            <div className="font-medium mb-0.5">Connect Google Drive</div>
                            <p className="text-xs text-gray-400">We’ll auto-create a folder called <span className="text-emerald-300">"OPERATR Restream Clips"</span> in your Drive root. Zapier drops your Restream clips into it — they appear here automatically.</p>
                          </div>
                          <button
                            type="button"
                            onClick={connectGdrive}
                            data-testid="automation-gdrive-connect"
                            className="text-sm px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 flex items-center gap-2 whitespace-nowrap"
                          >
                            <ExternalLink size={14} /> Connect Drive
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3" data-testid="automation-gdrive-connected">
                        <div className="flex items-center justify-between text-xs flex-wrap gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-400">✓ Drive connected</span>
                            {gdriveStatus.folder_url && (
                              <a href={gdriveStatus.folder_url} target="_blank" rel="noreferrer"
                                 className="text-cyan-400 hover:text-cyan-300 underline truncate max-w-xs"
                                 title={gdriveStatus.folder_name}>
                                {gdriveStatus.folder_name || 'OPERATR Restream Clips'} ↗
                              </a>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={pollGdrive}
                              disabled={gdrivePolling}
                              data-testid="automation-gdrive-sync"
                              className="px-2 py-1 rounded bg-emerald-700/40 border border-emerald-700 hover:bg-emerald-700/60 disabled:opacity-50 flex items-center gap-1 text-emerald-200"
                            >
                              {gdrivePolling ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                              Sync now → Auto-Clips
                            </button>
                            <button
                              type="button"
                              onClick={loadGdriveFiles}
                              data-testid="automation-gdrive-reload"
                              className="px-2 py-1 rounded border border-gray-700 hover:border-gray-400 flex items-center gap-1"
                            >
                              <RefreshCw size={11} /> Refresh
                            </button>
                            <button
                              type="button"
                              onClick={disconnectGdrive}
                              data-testid="automation-gdrive-disconnect"
                              className="px-2 py-1 rounded border border-red-800 text-red-300 hover:border-red-500"
                            >
                              Disconnect
                            </button>
                          </div>
                        </div>

                        {/* In-flight Descript imports */}
                        {descriptStatus.reachable && descriptProjects.length > 0 && (
                          <div className="bg-amber-950/20 border border-amber-900/60 rounded-lg p-3 space-y-2" data-testid="automation-descript-imports">
                            <div className="flex items-center justify-between">
                              <div className="text-[11px] uppercase tracking-wider text-amber-300 flex items-center gap-1.5">
                                <Wand2 size={11} /> Descript Imports
                              </div>
                              <button
                                type="button"
                                onClick={refreshDescriptProjects}
                                data-testid="automation-descript-refresh"
                                className="text-[10px] text-amber-300/70 hover:text-amber-200"
                              >
                                Refresh ↻
                              </button>
                            </div>
                            <ul className="space-y-1">
                              {descriptProjects.slice(0, 6).map(p => {
                                const importDone = p.state === 'imported';
                                const importErr = p.state === 'import_error';
                                const agentDone = p.agent_state === 'done';
                                const agentErr = p.agent_state === 'failed';
                                const stateLabel = importErr
                                  ? 'import failed'
                                  : !importDone
                                    ? `importing…`
                                    : p.agent_job_id
                                      ? (agentDone ? 'done' : agentErr ? 'agent failed' : (p.agent_state || 'running'))
                                      : 'imported';
                                const stateClass = (importErr || agentErr)
                                  ? 'text-red-400'
                                  : (importDone && (agentDone || !p.agent_job_id))
                                    ? 'text-emerald-400'
                                    : 'text-amber-300';
                                return (
                                  <li key={p.id} className="text-[11px] flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-black/40">
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate" title={p.name}>{p.name}</div>
                                      <div className="text-[9px] text-gray-500">
                                        {p.action} · <span className={stateClass}>{stateLabel}</span>
                                      </div>
                                      {p.agent_summary && (
                                        <div className="text-[9px] text-gray-400 mt-0.5 line-clamp-2" title={p.agent_summary}>
                                          {p.agent_summary}
                                        </div>
                                      )}
                                    </div>
                                    {p.editor_url && (
                                      <a
                                        href={p.editor_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[10px] px-2 py-1 rounded border border-amber-700 text-amber-200 hover:bg-amber-950/60 flex items-center gap-1 whitespace-nowrap"
                                      >
                                        Open ↗
                                      </a>
                                    )}
                                  </li>
                                );
                              })}
                              {descriptProjects.length > 6 && (
                                <li className="text-[10px] text-gray-500 text-center pt-1">+{descriptProjects.length - 6} more</li>
                              )}
                            </ul>
                          </div>
                        )}

                        {gdriveLoading ? (
                          <div className="text-xs text-gray-400 flex items-center gap-2 py-2">
                            <Loader2 size={12} className="animate-spin" /> Loading clips from Drive…
                          </div>
                        ) : gdriveFiles.length === 0 ? (
                          <div className="text-xs text-gray-400 py-3 space-y-3">
                            <p>No clips in your Drive folder yet.</p>
                            <details className="bg-black/40 border border-gray-800 rounded-lg p-3 group">
                              <summary className="cursor-pointer text-emerald-300 hover:text-emerald-200 select-none list-none flex items-center gap-1 text-[11px]">
                                <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
                                Set up Zapier (5 min walkthrough)
                              </summary>
                              <ol className="mt-2 ml-4 space-y-1.5 list-decimal text-[11px] leading-relaxed text-gray-300">
                                <li>Sign in at <a href="https://zapier.com" target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">zapier.com</a> (free tier works).</li>
                                <li>Click <strong>Create Zap</strong>.</li>
                                <li>For the <strong>Trigger</strong>, search and pick <strong>Restream</strong> → event <strong>"New Clip"</strong>. Connect your Restream account when prompted.</li>
                                <li>For the <strong>Action</strong>, search and pick <strong>Google Drive</strong> → event <strong>"Upload File"</strong>. Connect the same Google account you connected here.</li>
                                <li>In the action config:<br />
                                  • <strong>Drive</strong>: My Drive<br />
                                  • <strong>Folder</strong>: <span className="text-emerald-300">OPERATR Restream Clips</span> {gdriveStatus.folder_id && <span className="text-gray-500">(id: <code className="text-[10px]">{gdriveStatus.folder_id}</code>)</span>}<br />
                                  • <strong>File</strong>: map to Restream’s <em>Clip Video URL</em> (or <em>Download URL</em>) field — Zapier will fetch it and re-upload.<br />
                                  • <strong>File Name</strong>: map to Restream's <em>Clip Name</em> + ".mp4"<br />
                                  • <strong>Convert to Document</strong>: <strong>No</strong>
                                </li>
                                <li>Click <strong>Publish</strong>.</li>
                                <li>Generate a clip on Restream → wait ~1 min → click <strong>Sync now</strong> here. The clip appears in this list <em>and</em> in Media Portfolio → Auto-Clips automatically.</li>
                              </ol>
                            </details>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="automation-gdrive-files">
                            {gdriveFiles.map(f => (
                              <div key={f.file_id} className="border border-gray-800 rounded-lg overflow-hidden bg-black flex flex-col">
                                <button
                                  type="button"
                                  onClick={() => setWatchClip({
                                    clip: { name: f.name, transcript: null, virality_score: null, thumbnail_url: f.thumbnail_url },
                                    project: { title: 'Drive' },
                                    url: f.stream_url,
                                    type: 'video',
                                    fallback_iframe: f.preview_url,
                                  })}
                                  data-testid={`automation-gdrive-watch-${f.file_id}`}
                                  className="w-full aspect-video bg-gray-900 relative group"
                                >
                                  {f.thumbnail_url ? (
                                    <img src={f.thumbnail_url} alt={f.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-600 text-[10px]">no preview</div>
                                  )}
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Play size={32} className="text-white" />
                                  </div>
                                </button>
                                <div className="p-2 space-y-1 relative">
                                  <div className="text-[12px] line-clamp-2" title={f.name}>{f.name}</div>
                                  <div className="text-[10px] text-gray-500 flex items-center gap-2">
                                    {f.duration_sec ? <span>{Math.round(f.duration_sec)}s</span> : null}
                                    {f.size_bytes ? <span>· {(f.size_bytes / 1024 / 1024).toFixed(1)} MB</span> : null}
                                    {f.created_at ? <span>· {new Date(f.created_at).toLocaleDateString()}</span> : null}
                                  </div>
                                  {descriptStatus.reachable && (
                                    <div className="pt-1 relative">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDescriptMenuFor(prev => prev === f.file_id ? null : f.file_id);
                                        }}
                                        disabled={descriptSendingId === f.file_id}
                                        data-testid={`automation-descript-send-${f.file_id}`}
                                        className="w-full text-[10px] px-2 py-1 rounded-lg border border-amber-700 text-amber-200 hover:bg-amber-950/40 disabled:opacity-50 flex items-center justify-center gap-1"
                                      >
                                        {descriptSendingId === f.file_id
                                          ? <Loader2 size={10} className="animate-spin" />
                                          : <Wand2 size={10} />}
                                        Send to Descript ▾
                                      </button>
                                      {descriptMenuFor === f.file_id && (
                                        <div
                                          className="absolute left-0 right-0 bottom-full mb-1 bg-gray-950 border border-gray-700 rounded-lg shadow-xl z-20 overflow-hidden"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <button
                                            type="button"
                                            onClick={() => sendToDescript(f, 'auto-clips')}
                                            className="w-full text-left px-3 py-2 text-[11px] hover:bg-gray-800 flex items-center gap-2"
                                          >
                                            <Sparkles size={11} className="text-amber-400" />
                                            <div>
                                              <div className="text-gray-200">Auto-clips</div>
                                              <div className="text-[9px] text-gray-500">Underlord picks viral moments</div>
                                            </div>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => sendToDescript(f, 'studio-sound')}
                                            className="w-full text-left px-3 py-2 text-[11px] hover:bg-gray-800 flex items-center gap-2 border-t border-gray-800"
                                          >
                                            <Volume2 size={11} className="text-cyan-400" />
                                            <div>
                                              <div className="text-gray-200">Studio Sound + captions</div>
                                              <div className="text-[9px] text-gray-500">Clean audio + auto captions</div>
                                            </div>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => sendToDescript(f, 'edit')}
                                            className="w-full text-left px-3 py-2 text-[11px] hover:bg-gray-800 flex items-center gap-2 border-t border-gray-800"
                                          >
                                            <ExternalLink size={11} className="text-emerald-400" />
                                            <div>
                                              <div className="text-gray-200">Open in Descript editor</div>
                                              <div className="text-[9px] text-gray-500">Manual editing UI</div>
                                            </div>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  )}

                </div>
              )}
            </div>
          )}


          {/* Shared clip settings — apply to all three modes */}
          <NumField label="Max clips per video" value={cfg.max_clips_per_video} min={1} max={8}
                    testId="automation-max-clips"
                    onChange={(v) => setCfg({ ...cfg, max_clips_per_video: v })} />
          <NumField label="Clip duration (seconds)" value={cfg.clip_duration_sec} min={10} max={120}
                    testId="automation-clip-dur"
                    onChange={(v) => setCfg({ ...cfg, clip_duration_sec: v })} />

          <label className="flex items-center gap-3 md:col-span-2 mt-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!cfg.vertical}
              onChange={(e) => setCfg({ ...cfg, vertical: e.target.checked })}
              data-testid="automation-vertical"
              className="w-4 h-4 accent-cyan-500"
            />
            <span className="text-sm">Render clips as 9:16 vertical (TikTok / Reels / Shorts)</span>
          </label>

          {inputMode === 'playlist' && (
            <label className="flex items-center gap-3 md:col-span-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                data-testid="automation-enabled"
                className="w-4 h-4 accent-emerald-500"
              />
              <span className="text-sm flex items-center gap-2">
                <Power size={14} className={cfg.enabled ? 'text-emerald-400' : 'text-gray-500'} />
                Enable daily schedule
              </span>
            </label>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-5">
          <button
            type="button"
            onClick={() => saveCfg()}
            disabled={saving}
            data-testid="automation-save"
            className="text-sm px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
          </button>

          {inputMode === 'playlist' && (
            <button
              type="button"
              onClick={runNow}
              disabled={running || !!activeRun}
              data-testid="automation-run-now"
              className="text-sm px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 flex items-center gap-2 disabled:bg-gray-700 disabled:opacity-60"
            >
              {(running || activeRun) ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {activeRun ? 'Pipeline running…' : 'Run now (playlist)'}
            </button>
          )}
          {inputMode === 'url' && (
            <button
              type="button"
              onClick={runSingleUrl}
              disabled={running || !!activeRun || !singleUrl.trim()}
              data-testid="automation-run-url"
              className="text-sm px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 flex items-center gap-2 disabled:bg-gray-700 disabled:opacity-60"
            >
              {(running || activeRun) ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {activeRun ? 'Pipeline running…' : 'Clip this video'}
            </button>
          )}
          {inputMode === 'upload' && (
            <button
              type="button"
              onClick={runUpload}
              disabled={uploadingFile || !!activeRun || !uploadFile}
              data-testid="automation-run-upload"
              className="text-sm px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 flex items-center gap-2 disabled:bg-gray-700 disabled:opacity-60"
            >
              {(uploadingFile || activeRun) ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              {uploadingFile ? `Uploading ${uploadProgress}%` : activeRun ? 'Pipeline running…' : 'Upload & clip'}
            </button>
          )}

          <button
            type="button"
            onClick={loadAll}
            data-testid="automation-refresh"
            className="text-sm px-3 py-2 rounded-lg border border-gray-700 hover:border-gray-400 flex items-center gap-2"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Active run / latest runs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-5" data-testid="automation-runs">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} className="text-cyan-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300">Recent runs</h3>
          </div>
          {runs.length === 0 ? (
            <p className="text-xs text-gray-500">No runs yet. Click <span className="text-gray-300">Run now</span> to test the pipeline.</p>
          ) : (
            <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {runs.map(r => (
                <li key={r.id}
                    className={`text-xs border rounded-lg px-3 py-2 ${
                      r.status === 'running' ? 'border-cyan-700 bg-cyan-950/30' :
                      r.status === 'error' ? 'border-red-800 bg-red-950/20' :
                      'border-gray-800'
                    }`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Clock size={11} className="text-gray-500" />
                      {fmtDate(r.started_at)}
                    </span>
                    <span className={`uppercase tracking-wider text-[10px] font-semibold ${
                      r.status === 'running' ? 'text-cyan-400' :
                      r.status === 'error' ? 'text-red-400' :
                      'text-emerald-400'
                    }`}>{r.status}</span>
                  </div>
                  <div className="text-gray-400 mt-1">
                    {r.videos_processed || 0} video(s) · {r.clips_created || 0} clip(s){r.trigger === 'schedule' ? ' · scheduled' : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-gray-950 border border-gray-800 rounded-xl p-5" data-testid="automation-log">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300">
              {activeRun ? 'Live log' : runs[0] ? 'Last run log' : 'Run log'}
            </h3>
          </div>
          {(() => {
            const target = activeRun || runs[0];
            if (!target) return <p className="text-xs text-gray-500">No log yet.</p>;
            const lines = target.log || [];
            return (
              <div className="bg-black/60 rounded-lg p-3 max-h-72 overflow-y-auto font-mono text-[11px] leading-relaxed">
                {lines.length === 0 ? (
                  <span className="text-gray-500">Waiting for output…</span>
                ) : lines.map((l, i) => (
                  <div key={i} className="text-gray-300 whitespace-pre-wrap break-words">{l}</div>
                ))}
                {target.status === 'error' && target.error && (
                  <div className="text-red-400 mt-2">Error: {target.error}</div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Auto-clip gallery */}
      <div className="bg-gray-950 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Film size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-300">Auto-Clips</h3>
          </div>
          <span className="text-[11px] text-gray-500">{clips.length} clip(s)</span>
        </div>
        {clips.length === 0 ? (
          <p className="text-xs text-gray-500">No auto-clips yet. A successful run will populate this gallery.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="automation-clips">
            {clips.map(c => (
              <div key={c.id} className="border border-gray-800 rounded-lg overflow-hidden bg-black">
                <video
                  src={`${API_BASE}${c.url}`}
                  controls
                  preload="metadata"
                  className={`w-full ${c.vertical ? 'aspect-[9/16]' : 'aspect-video'} bg-black`}
                />
                <div className="p-2 space-y-1">
                  <div className="text-[12px] text-gray-200 line-clamp-2" title={c.title}>{c.title}</div>
                  {c.highlight_reason && (
                    <div className="text-[10px] text-amber-300/80 line-clamp-2" title={c.highlight_reason}>
                      ✨ {c.highlight_reason}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <a
                      href={`https://www.youtube.com/watch?v=${c.source_video_id}`}
                      target="_blank" rel="noreferrer"
                      className="text-[10px] text-gray-500 hover:text-gray-300 flex items-center gap-1"
                    >
                      Source <ExternalLink size={9} />
                    </a>
                    <button
                      type="button"
                      onClick={() => markPosted(c, !c.posted)}
                      data-testid={`automation-mark-posted-${c.id}`}
                      className={`text-[10px] px-2 py-1 rounded border ${
                        c.posted
                          ? 'border-emerald-600 text-emerald-300 bg-emerald-950/30'
                          : 'border-gray-700 text-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {c.posted ? '✓ Posted' : 'Mark posted'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Watch-clip modal */}
      {watchClip && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setWatchClip(null)}
          data-testid="automation-watch-modal"
        >
          <div
            className="bg-gray-950 border border-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-800">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate" title={watchClip.clip.name}>{watchClip.clip.name}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  from <span className="text-gray-300">{watchClip.project.title}</span>
                  {watchClip.clip.virality_score != null && (
                    <> · <span className="text-amber-300">✨ {watchClip.clip.virality_score}</span></>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setWatchClip(null)}
                data-testid="automation-watch-close"
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {watchLoading || !watchClip.url ? (
                <div className="aspect-video flex items-center justify-center text-gray-400">
                  <Loader2 size={20} className="animate-spin mr-2" /> Resolving playback URL…
                </div>
              ) : watchClip.type === 'video' ? (
                <video
                  src={watchClip.url}
                  controls
                  autoPlay
                  className="w-full max-h-[60vh] bg-black"
                  data-testid="automation-watch-video"
                  onError={(e) => {
                    // If the direct stream fails (Drive virus-scan interstitial,
                    // large file, etc.) but we have an iframe URL — swap in.
                    if (watchClip.fallback_iframe) {
                      setWatchClip(prev => ({ ...prev, url: prev.fallback_iframe, type: 'iframe' }));
                    }
                  }}
                />
              ) : watchClip.type === 'iframe' ? (
                <iframe
                  src={watchClip.url}
                  title={watchClip.clip.name}
                  className="w-full aspect-video bg-black"
                  allow="autoplay; fullscreen"
                  data-testid="automation-watch-iframe"
                />
              ) : (
                <div className="p-6 text-center space-y-3">
                  {watchClip.clip.thumbnail_url && (
                    <img src={watchClip.clip.thumbnail_url} alt="" className="mx-auto rounded-lg max-h-72" />
                  )}
                  <p className="text-sm text-gray-300">
                    Restream doesn’t expose a direct video URL for this clip via API. You can watch and download it from Restream’s own player:
                  </p>
                  <a
                    href={watchClip.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="automation-watch-external"
                    className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500"
                  >
                    <ExternalLink size={14} /> Open in Restream
                  </a>
                </div>
              )}
              {watchClip.clip.transcript && (
                <div className="p-4 border-t border-gray-800 max-h-44 overflow-y-auto">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Transcript</div>
                  <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">{watchClip.clip.transcript}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SourceModeBtn = ({ active, onClick, icon: Icon, title, sub, warn, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left p-3 rounded-lg border transition-colors flex items-start gap-3 ${
      active
        ? 'border-cyan-500 bg-cyan-950/30'
        : 'border-gray-800 hover:border-gray-600 bg-black'
    }`}
  >
    <Icon size={18} className={active ? 'text-cyan-300 mt-0.5' : 'text-gray-400 mt-0.5'} />
    <div className="flex-1 min-w-0">
      <div className={`text-sm font-medium ${active ? 'text-white' : 'text-gray-200'}`}>{title}</div>
      <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>
      {warn && (
        <div className="text-[10px] text-amber-400 mt-1">Cookies recommended</div>
      )}
    </div>
  </button>
);

const NumField = ({ label, value, min, max, onChange, testId }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    <input
      type="number"
      min={min}
      max={max}
      value={value ?? ''}
      data-testid={testId}
      onChange={(e) => {
        const n = parseInt(e.target.value || '0', 10);
        if (Number.isNaN(n)) return;
        onChange(Math.max(min, Math.min(max, n)));
      }}
      className="w-full bg-black border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-400"
    />
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
};
